import type { GridCoordinates, PixelCoordinates } from '@/types'

/**
 * Convert grid coordinates to pixel coordinates
 * @param gridX - Grid x coordinate
 * @param gridY - Grid y coordinate
 * @param gridSize - Size of each grid square in pixels
 * @returns Pixel coordinates (top-left corner of the grid square)
 */
export function gridToPixel(
  gridX: number,
  gridY: number,
  gridSize: number
): PixelCoordinates {
  return {
    x: gridX * gridSize,
    y: gridY * gridSize,
  }
}

/**
 * Convert pixel coordinates to grid coordinates
 * @param pixelX - Pixel x coordinate
 * @param pixelY - Pixel y coordinate
 * @param gridSize - Size of each grid square in pixels
 * @returns Grid coordinates (snapped to nearest grid intersection)
 */
export function pixelToGrid(
  pixelX: number,
  pixelY: number,
  gridSize: number
): GridCoordinates {
  return {
    x: Math.floor(pixelX / gridSize),
    y: Math.floor(pixelY / gridSize),
  }
}

/**
 * Snap pixel coordinates to the nearest grid intersection
 * @param pixelX - Pixel x coordinate
 * @param pixelY - Pixel y coordinate
 * @param gridSize - Size of each grid square in pixels
 * @returns Snapped pixel coordinates
 */
export function snapToGrid(
  pixelX: number,
  pixelY: number,
  gridSize: number
): PixelCoordinates {
  const grid = pixelToGrid(pixelX, pixelY, gridSize)
  return gridToPixel(grid.x, grid.y, gridSize)
}

/**
 * Check if grid coordinates are within map bounds
 * @param gridX - Grid x coordinate
 * @param gridY - Grid y coordinate
 * @param mapWidth - Map width in grid squares
 * @param mapHeight - Map height in grid squares
 * @returns True if coordinates are within bounds
 */
export function isWithinBounds(
  gridX: number,
  gridY: number,
  mapWidth: number,
  mapHeight: number
): boolean {
  return gridX >= 0 && gridX < mapWidth && gridY >= 0 && gridY < mapHeight
}

/**
 * Clamp grid coordinates to map bounds
 * @param gridX - Grid x coordinate
 * @param gridY - Grid y coordinate
 * @param mapWidth - Map width in grid squares
 * @param mapHeight - Map height in grid squares
 * @returns Clamped grid coordinates
 */
export function clampToMapBounds(
  gridX: number,
  gridY: number,
  mapWidth: number,
  mapHeight: number
): GridCoordinates {
  return {
    x: Math.max(0, Math.min(gridX, mapWidth - 1)),
    y: Math.max(0, Math.min(gridY, mapHeight - 1)),
  }
}
